import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useQuery } from '@tanstack/react-query';
import { FiRefreshCw } from 'react-icons/fi';
import { MdArchive, MdDownload, MdHourglassBottom, MdLock } from 'react-icons/md';
import { contractsService } from '../../../services/contracts.service';
import {
  ContractAddendumSummary,
  ContractSummary,
} from '../../../services/types/contracts.type';

// ───────────────────────────────────────────────────────────────────────────
// Tradução de enums vindos do backend
// ───────────────────────────────────────────────────────────────────────────

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Aguardando assinatura',
  SIGNED: 'Assinado',
  SUPERSEDED: 'Substituído',
  CANCELED: 'Cancelado',
};

const ADDENDUM_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Aguardando assinatura',
  SIGNED: 'Assinado',
  CANCELED: 'Cancelado',
};

const ADDENDUM_REASON_LABEL: Record<string, string> = {
  PLAN_CHANGE: 'Mudança de plano',
};

// ─── status visual map ────────────────────────────────────────────────────

interface StatusStyle {
  bg: string;
  border: string;
  ink: string;
  glyph: string;
}

const STATUS_STYLES: Record<string, StatusStyle> = {
  PENDING: {
    bg: '#fff8e1',
    border: '#c89d3a',
    ink: '#7a5510',
    glyph: '⏳',
  },
  SIGNED: {
    bg: '#e7efe7',
    border: '#3d6b3a',
    ink: '#1f3a1d',
    glyph: '✓',
  },
  SUPERSEDED: {
    bg: '#eeeae3',
    border: '#8a7b62',
    ink: '#4a3f30',
    glyph: '∞',
  },
  CANCELED: {
    bg: '#f4e3e3',
    border: '#8a3a3a',
    ink: '#5b1f1f',
    glyph: '×',
  },
};

const FALLBACK_STATUS: StatusStyle = STATUS_STYLES.SUPERSEDED;

// ─── helpers ──────────────────────────────────────────────────────────────

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const shortId = (id: string) => id.slice(0, 8).toUpperCase();

// ─── animations ───────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const stamp = keyframes`
  0%   { transform: rotate(-8deg) scale(1); }
  100% { transform: rotate(-8deg) scale(1); }
`;

// ─── components ───────────────────────────────────────────────────────────

function StatusSeal({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || FALLBACK_STATUS;
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      w="68px"
      h="68px"
      borderRadius="full"
      border="2px solid"
      borderColor={style.border}
      bg={style.bg}
      color={style.ink}
      transform="rotate(-8deg)"
      animation={`${stamp} 0.4s ease-out`}
      flexShrink={0}
      boxShadow="0 1px 0 rgba(0,0,0,0.04)"
    >
      <Text fontSize="22px" lineHeight="1" mt="2px">
        {style.glyph}
      </Text>
      <Text
        fontSize="7px"
        fontWeight="bold"
        letterSpacing="0.1em"
        textTransform="uppercase"
        mt="2px"
        textAlign="center"
        px="4px"
      >
        {(CONTRACT_STATUS_LABEL[status] || status).split(' ')[0]}
      </Text>
    </Flex>
  );
}

function PageMastHead({ isFetching, refetch }: { isFetching: boolean; refetch: () => void }) {
  const inkColor = useColorModeValue('#1c1916', '#f5f1ea');
  const muted = useColorModeValue('#7a6f60', '#a89c89');
  return (
    <Box position="relative" mb={10}>
      <Text
        fontSize="11px"
        letterSpacing="0.32em"
        textTransform="uppercase"
        color={muted}
        fontWeight="600"
      >
        Documentos jurídicos
      </Text>
      <HStack align="baseline" spacing={3} mt={2}>
        <Heading
          as="h1"
          fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
          fontWeight="500"
          fontSize={{ base: '38px', md: '54px' }}
          lineHeight="0.95"
          color={inkColor}
          letterSpacing="-0.02em"
        >
          Meus Contratos
        </Heading>
        <IconButton
          aria-label="Atualizar dados"
          icon={<FiRefreshCw />}
          size="xs"
          variant="ghost"
          isLoading={isFetching}
          onClick={() => refetch()}
        />
      </HStack>
      <Box
        mt={4}
        h="1px"
        bgGradient="linear(to-r, blackAlpha.500, transparent)"
        maxW="120px"
      />
      <Text mt={4} fontSize="sm" color={muted} maxW="540px" lineHeight="1.6">
        Histórico completo dos contratos assinados eletronicamente, incluindo
        termos aditivos. Cada documento pode ser baixado em PDF a qualquer momento.
      </Text>
    </Box>
  );
}

function SectionTitle({ children, count }: { children: React.ReactNode; count: number }) {
  const inkColor = useColorModeValue('#2a241f', '#efe9dd');
  const muted = useColorModeValue('#988a78', '#897e6c');
  return (
    <HStack align="baseline" spacing={3} mb={5}>
      <Heading
        as="h2"
        fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
        fontSize="24px"
        fontWeight="500"
        color={inkColor}
        letterSpacing="-0.01em"
      >
        {children}
      </Heading>
      <Text
        fontSize="11px"
        color={muted}
        letterSpacing="0.18em"
        textTransform="uppercase"
        fontWeight="600"
      >
        {count} {count === 1 ? 'registro' : 'registros'}
      </Text>
    </HStack>
  );
}

interface ContractCardProps {
  contract: ContractSummary;
  index: number;
  onDownload: (id: string) => void;
}

function ContractCard({ contract, index, onDownload }: ContractCardProps) {
  const surface = useColorModeValue('#fefcf7', '#1f1b16');
  const border = useColorModeValue('rgba(28, 25, 22, 0.12)', 'rgba(245, 241, 234, 0.12)');
  const innerBorder = useColorModeValue('rgba(28, 25, 22, 0.07)', 'rgba(245, 241, 234, 0.07)');
  const ink = useColorModeValue('#1c1916', '#f5f1ea');
  const muted = useColorModeValue('#7a6f60', '#a89c89');
  const accent = useColorModeValue('#3d2817', '#d4b896');

  const signedDate = formatDate(contract.signedAt);
  const signedTime = formatTime(contract.signedAt);
  const createdDate = formatDate(contract.createdAt);
  const statusLabel = CONTRACT_STATUS_LABEL[contract.status] || contract.status;

  return (
    <Box
      position="relative"
      bg={surface}
      border="1px solid"
      borderColor={border}
      borderRadius="2px"
      p={{ base: 5, md: 7 }}
      animation={`${fadeUp} 0.5s ease-out ${index * 0.06}s both`}
      _hover={{
        borderColor: accent,
        boxShadow: '0 10px 30px -18px rgba(28, 25, 22, 0.4)',
      }}
      transition="all 0.25s ease-out"
      overflow="hidden"
    >
      {/* faux notch / corner ornament */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="34px"
        h="34px"
        borderRight="1px solid"
        borderBottom="1px solid"
        borderColor={innerBorder}
      />
      <Box
        position="absolute"
        bottom="0"
        right="0"
        w="34px"
        h="34px"
        borderLeft="1px solid"
        borderTop="1px solid"
        borderColor={innerBorder}
      />

      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'flex-start', md: 'center' }}
        gap={{ base: 5, md: 6 }}
      >
        <StatusSeal status={contract.status} />

        <Box flex="1" minW={0}>
          <HStack spacing={3} mb={1}>
            <Text
              fontSize="10px"
              fontFamily='"JetBrains Mono", "SF Mono", Menlo, monospace'
              letterSpacing="0.12em"
              color={muted}
            >
              N.º {shortId(contract.id)} · v{contract.templateVersion}
            </Text>
          </HStack>

          <Heading
            as="h3"
            fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
            fontSize="22px"
            fontWeight="500"
            color={ink}
            lineHeight="1.15"
            letterSpacing="-0.01em"
          >
            Contrato de Prestação de Serviços
          </Heading>

          <HStack
            mt={3}
            spacing={4}
            divider={
              <Box
                w="1px"
                h="28px"
                bg={innerBorder}
                role="separator"
              />
            }
            flexWrap="wrap"
          >
            <VStack align="flex-start" spacing={0}>
              <Text
                fontSize="9px"
                letterSpacing="0.18em"
                textTransform="uppercase"
                color={muted}
                fontWeight="700"
              >
                Situação
              </Text>
              <Text fontSize="13px" color={ink} fontWeight="500">
                {statusLabel}
              </Text>
            </VStack>

            {signedDate ? (
              <VStack align="flex-start" spacing={0}>
                <Text
                  fontSize="9px"
                  letterSpacing="0.18em"
                  textTransform="uppercase"
                  color={muted}
                  fontWeight="700"
                >
                  Assinado em
                </Text>
                <Text fontSize="13px" color={ink} fontWeight="500">
                  {signedDate}
                  {signedTime && (
                    <Text as="span" color={muted} ml={1.5}>
                      às {signedTime}
                    </Text>
                  )}
                </Text>
              </VStack>
            ) : (
              <VStack align="flex-start" spacing={0}>
                <Text
                  fontSize="9px"
                  letterSpacing="0.18em"
                  textTransform="uppercase"
                  color={muted}
                  fontWeight="700"
                >
                  Emitido em
                </Text>
                <Text fontSize="13px" color={ink} fontWeight="500">
                  {createdDate || '—'}
                </Text>
              </VStack>
            )}
          </HStack>
        </Box>

        <Box>
          {contract.pdfS3Key ? (
            <Button
              onClick={() => onDownload(contract.id)}
              variant="outline"
              borderColor={accent}
              color={accent}
              borderRadius="2px"
              px={5}
              py={6}
              fontSize="12px"
              fontWeight="600"
              letterSpacing="0.1em"
              textTransform="uppercase"
              leftIcon={<Icon as={MdDownload} boxSize="14px" />}
              _hover={{
                bg: accent,
                color: surface,
              }}
              transition="all 0.2s"
            >
              Baixar PDF
            </Button>
          ) : (
            <HStack
              color={muted}
              fontSize="11px"
              fontStyle="italic"
              spacing={2}
              letterSpacing="0.04em"
            >
              <Icon as={MdHourglassBottom} boxSize="14px" />
              <Text>PDF em geração</Text>
            </HStack>
          )}
        </Box>
      </Flex>
    </Box>
  );
}

interface AddendumCardProps {
  addendum: ContractAddendumSummary;
  index: number;
  onDownload: (id: string) => void;
}

function AddendumCard({ addendum, index, onDownload }: AddendumCardProps) {
  const surface = useColorModeValue('#fbf8f0', '#1a1612');
  const border = useColorModeValue('rgba(28, 25, 22, 0.1)', 'rgba(245, 241, 234, 0.1)');
  const ink = useColorModeValue('#2a241f', '#efe9dd');
  const muted = useColorModeValue('#7a6f60', '#a89c89');
  const accent = useColorModeValue('#3d2817', '#d4b896');

  const signedDate = formatDate(addendum.signedAt);
  const reasonLabel = ADDENDUM_REASON_LABEL[addendum.reason] || addendum.reason;
  const statusLabel = ADDENDUM_STATUS_LABEL[addendum.status] || addendum.status;

  return (
    <Flex
      align="center"
      gap={5}
      bg={surface}
      border="1px solid"
      borderColor={border}
      borderLeft="3px solid"
      borderLeftColor={accent}
      borderRadius="2px"
      px={{ base: 4, md: 6 }}
      py={5}
      animation={`${fadeUp} 0.45s ease-out ${index * 0.05}s both`}
      _hover={{
        borderColor: accent,
        borderLeftColor: accent,
      }}
      transition="all 0.2s ease-out"
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
    >
      <Box flexShrink={0}>
        <Icon as={MdArchive} boxSize="22px" color={accent} />
      </Box>

      <Box flex="1" minW={0}>
        <Text
          fontSize="9px"
          letterSpacing="0.18em"
          textTransform="uppercase"
          color={muted}
          fontWeight="700"
          mb={1}
        >
          Termo Aditivo · N.º {shortId(addendum.id)}
        </Text>
        <Text
          fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
          fontSize="17px"
          color={ink}
          fontWeight="500"
        >
          {reasonLabel}
        </Text>
        <Text fontSize="12px" color={muted} mt={1}>
          {statusLabel}
          {signedDate && ` · ${signedDate}`}
        </Text>
      </Box>

      <Box>
        {addendum.pdfS3Key ? (
          <Button
            onClick={() => onDownload(addendum.id)}
            size="sm"
            variant="outline"
            borderColor={accent}
            color={accent}
            borderRadius="2px"
            fontSize="11px"
            fontWeight="600"
            letterSpacing="0.08em"
            textTransform="uppercase"
            leftIcon={<Icon as={MdDownload} boxSize="12px" />}
            _hover={{ bg: accent, color: surface }}
          >
            Baixar
          </Button>
        ) : (
          <Text fontSize="10px" color={muted} fontStyle="italic">
            em geração
          </Text>
        )}
      </Box>
    </Flex>
  );
}

function EmptyState({ icon, message }: { icon: any; message: string }) {
  const surface = useColorModeValue('#faf6ed', '#1a1612');
  const border = useColorModeValue('rgba(28, 25, 22, 0.1)', 'rgba(245, 241, 234, 0.1)');
  const muted = useColorModeValue('#988a78', '#897e6c');
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      bg={surface}
      border="1px dashed"
      borderColor={border}
      borderRadius="2px"
      py={12}
      px={6}
    >
      <Icon as={icon} boxSize="32px" color={muted} mb={3} />
      <Text
        fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
        fontSize="15px"
        color={muted}
        fontStyle="italic"
      >
        {message}
      </Text>
    </Flex>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────

export function ContractsListPage() {
  const pageBg = useColorModeValue('#faf6ed', '#100e0b');
  const grainOverlay = useColorModeValue(
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.06 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.04 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  );

  const { data: contracts, isLoading, isFetching, refetch } = useQuery<ContractSummary[]>(
    ['contracts', 'me', 'list'],
    () => contractsService.listMine(),
  );

  const handleDownloadContract = async (id: string) => {
    try {
      const url = await contractsService.getDownloadUrl(id);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Erro ao baixar contrato', err);
    }
  };

  const handleDownloadAddendum = async (id: string) => {
    try {
      const url = await contractsService.getAddendumDownloadUrl(id);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Erro ao baixar termo aditivo', err);
    }
  };

  const safeContracts = contracts ?? [];
  const allAddendums = safeContracts.flatMap((c) =>
    (c.addendums ?? []).map((a) => ({ ...a, parentContractVersion: c.templateVersion })),
  );

  return (
    <Box
      minH="100vh"
      bg={pageBg}
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: grainOverlay,
        pointerEvents: 'none',
        opacity: 0.7,
        mixBlendMode: 'multiply',
      }}
    >
      <Box maxW="980px" mx="auto" px={{ base: 4, md: 8 }} pb={16} position="relative">
        {isLoading ? (
          <Flex justify="center" py={20}>
            <Spinner size="lg" color="#3d2817" />
          </Flex>
        ) : (
          <>
            <PageMastHead isFetching={isFetching} refetch={refetch} />

            <Stack spacing={12}>
              <Box>
                <SectionTitle count={safeContracts.length}>Contratos</SectionTitle>
                {safeContracts.length === 0 ? (
                  <EmptyState icon={MdLock} message="Nenhum contrato registrado até o momento." />
                ) : (
                  <Stack spacing={4}>
                    {safeContracts.map((c, i) => (
                      <ContractCard
                        key={c.id}
                        contract={c}
                        index={i}
                        onDownload={handleDownloadContract}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <SectionTitle count={allAddendums.length}>Termos Aditivos</SectionTitle>
                {allAddendums.length === 0 ? (
                  <EmptyState icon={MdArchive} message="Nenhum termo aditivo registrado." />
                ) : (
                  <Stack spacing={3}>
                    {allAddendums.map((a, i) => (
                      <AddendumCard
                        key={a.id}
                        addendum={a}
                        index={i}
                        onDownload={handleDownloadAddendum}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
